import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Box,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../context";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useAdd from "../../../../../api/useAdd";
import { useQueryClient } from "@tanstack/react-query";

const TabPanel = ({ children, value, index }) => {
  return value === index && <Box sx={{ pt: 2 }}>{children}</Box>;
};

const ClinicalNoteDialog = ({
  open,
  onClose,
  patientId,
  doctorId,
  encounterId = null,
  ticketId = null,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const queryClient = useQueryClient();

  const { data: encountersData } = useFetchObjects(
    patientId ? ["encounters", patientId] : null,
    patientId
      ? `encounters/?patient=${patientId}${ticketId ? `&ticket=${ticketId}` : ""}`
      : "",
    token,
  );

  const { handleAdd, loading, resetForm } = useAdd(
    "clinical-notes",
    token,
    t("profile.noteAdded") || "Clinical note created successfully",
    t("profile.noteAddFailed") || "Failed to create clinical note",
  );

  const [formData, setFormData] = useState({
    encounter: encounterId,
    note_type: 1, // PROGRESS_NOTE
    content: "",
    subjective: "",
    objective: "",
    assessment: "",
    plan: "",
  });
  const [noteFormat, setNoteFormat] = useState(0); // 0 = Simple, 1 = SOAP
  const [errors, setErrors] = useState({});

  const encounters = Array.isArray(encountersData?.results)
    ? encountersData.results
    : Array.isArray(encountersData)
      ? encountersData
      : [];

  useEffect(() => {
    if (resetForm) {
      setFormData({
        encounter: encounterId,
        note_type: 1,
        content: "",
        subjective: "",
        objective: "",
        assessment: "",
        plan: "",
      });
      setNoteFormat(0);
      setErrors({});
      onClose();
      queryClient.invalidateQueries(["unified-ehr", patientId]);
    }
  }, [resetForm, onClose, queryClient, patientId, encounterId]);

  // Auto-select or create encounter if ticketId provided
  useEffect(() => {
    if (ticketId && encounters.length > 0 && !formData.encounter) {
      // Try to find encounter for this ticket
      const ticketEncounter = encounters.find(
        (enc) => enc.ticket === parseInt(ticketId),
      );
      if (ticketEncounter) {
        setFormData({ ...formData, encounter: ticketEncounter.id });
      } else if (encounters.length > 0) {
        // Use most recent encounter
        setFormData({ ...formData, encounter: encounters[0].id });
      }
    }
  }, [ticketId, encounters]);

  const handleSubmit = () => {
    const newErrors = {};
    if (!formData.encounter) {
      newErrors.encounter =
        t("profile.encounterRequired") ||
        "Please select or create an encounter first";
    }
    if (noteFormat === 0 && !formData.content.trim()) {
      newErrors.content = t("profile.contentRequired") || "Content is required";
    }
    if (
      noteFormat === 1 &&
      !formData.subjective.trim() &&
      !formData.objective.trim()
    ) {
      newErrors.soap =
        t("profile.soapRequired") ||
        "Please fill at least Subjective or Objective";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const submitData = {
      encounter: formData.encounter,
      ticket: ticketId || null,
      note_type: formData.note_type,
      content:
        noteFormat === 0
          ? formData.content
          : `S: ${formData.subjective || "N/A"}\nO: ${formData.objective || "N/A"}\nA: ${formData.assessment || "N/A"}\nP: ${formData.plan || "N/A"}`,
      subjective: formData.subjective,
      objective: formData.objective,
      assessment: formData.assessment,
      plan: formData.plan,
    };

    handleAdd(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t("profile.addClinicalNote") || "Add Clinical Note"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("profile.encounter") || "Encounter"}</InputLabel>
              <Select
                value={formData.encounter || ""}
                onChange={(e) =>
                  setFormData({ ...formData, encounter: e.target.value })
                }
                label={t("profile.encounter") || "Encounter"}
                error={!!errors.encounter}
              >
                {encounters.map((enc) => (
                  <MenuItem key={enc.id} value={enc.id}>
                    {enc.get_encounter_type_display || "Encounter"} #{enc.id} -{" "}
                    {new Date(enc.encounter_date).toLocaleDateString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.encounter && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {errors.encounter}
              </Alert>
            )}
            {encounters.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t("profile.createEncounterFirst") ||
                  "Please create an encounter first"}
              </Alert>
            )}
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("profile.noteType") || "Note Type"}</InputLabel>
              <Select
                value={formData.note_type}
                onChange={(e) =>
                  setFormData({ ...formData, note_type: e.target.value })
                }
                label={t("profile.noteType") || "Note Type"}
              >
                <MenuItem value={1}>
                  {t("profile.progressNote") || "Progress Note"}
                </MenuItem>
                <MenuItem value={2}>
                  {t("profile.consultationNote") || "Consultation Note"}
                </MenuItem>
                <MenuItem value={3}>
                  {t("profile.procedureNote") || "Procedure Note"}
                </MenuItem>
                <MenuItem value={6}>{t("profile.other") || "Other"}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Tabs
              value={noteFormat}
              onChange={(e, newValue) => setNoteFormat(newValue)}
            >
              <Tab label={t("profile.simple") || "Simple"} />
              <Tab label={t("profile.soap") || "SOAP Format"} />
            </Tabs>
          </Grid>

          <TabPanel value={noteFormat} index={0}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("profile.content") || "Content"}
                multiline
                rows={6}
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                error={!!errors.content}
                helperText={errors.content}
                required
              />
            </Grid>
          </TabPanel>

          <TabPanel value={noteFormat} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("profile.subjective") || "Subjective (S)"}
                  multiline
                  rows={2}
                  value={formData.subjective}
                  onChange={(e) =>
                    setFormData({ ...formData, subjective: e.target.value })
                  }
                  placeholder={
                    t("profile.patientComplaints") ||
                    "Patient's complaints and symptoms"
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("profile.objective") || "Objective (O)"}
                  multiline
                  rows={2}
                  value={formData.objective}
                  onChange={(e) =>
                    setFormData({ ...formData, objective: e.target.value })
                  }
                  placeholder={
                    t("profile.examinationFindings") ||
                    "Physical examination findings"
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("profile.assessment") || "Assessment (A)"}
                  multiline
                  rows={2}
                  value={formData.assessment}
                  onChange={(e) =>
                    setFormData({ ...formData, assessment: e.target.value })
                  }
                  placeholder={
                    t("profile.clinicalAssessment") ||
                    "Clinical assessment and diagnosis"
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("profile.plan") || "Plan (P)"}
                  multiline
                  rows={2}
                  value={formData.plan}
                  onChange={(e) =>
                    setFormData({ ...formData, plan: e.target.value })
                  }
                  placeholder={
                    t("profile.treatmentPlan") ||
                    "Treatment plan and management"
                  }
                />
              </Grid>
            </Grid>
            {errors.soap && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errors.soap}
              </Alert>
            )}
          </TabPanel>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("profile.cancel") || "Cancel"}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.encounter}
        >
          {loading
            ? t("profile.saving") || "Saving..."
            : t("profile.add") || "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClinicalNoteDialog;
